/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, Row, Stack } from 'react-bootstrap'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  password: yup.string()
    .required('A senha é obrigatória')
    .default('')
    .min(5, 'A senha deve ter no mínimo 5 caracteres')
    .max(255, 'A senha deve ter no máximo 255 caracteres'),
  passwordConfirmation: yup.string()
    .required('A confirmação de senha é obrigatória')
    .default('')
    .oneOf([yup.ref('password')], 'As senhas não conferem')
    .min(5, 'A senha deve ter no mínimo 5 caracteres')
    .max(255, 'A senha deve ter no máximo 255 caracteres')
    .test('passwordConfirmation', 'As senhas não conferem', function (value) {
      const { password } = this.parent
      return password === value
    })
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormTogglePasswordHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onCancel?: () => void,
  onSubmit?: FormTogglePasswordHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean
}

/**
 * FormTogglePassword is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormTogglePassword from './src/views/PostEdit/FormTogglePassword.tsx'
 *
 * const MyComponent = () => (
 *   <FormTogglePassword />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormTogglePassword: FC<Props> = ({
  onSubmit = () => {},
  onCancel = () => {},
  initialValues = {},
  isLoading = false,
}) => {
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Nova senha *</Form.Label>
          <Form.Control
            size="sm"
            type='password'
            placeholder='Digite'
            name='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
          <Form.Text id="passwordHelpBlock" muted>
            {formik.touched.password && formik.errors.password}
          </Form.Text>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Confirme a nova senha *</Form.Label>
          <Form.Control
            size="sm"
            type='password'
            placeholder='Digite'
            name='passwordConfirmation'
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
          <Form.Text id="passwordHelpBlock" muted>
            {formik.touched.passwordConfirmation && formik.errors.passwordConfirmation}
          </Form.Text>
        </Form.Group>
        <Col md={12}>
          <Stack direction='horizontal' gap={2}>
            <div className="p-2">
              <Button
                variant="secondary"
                style={{ marginRight: 10 }} onClick={onCancel}
                disabled={formik.isSubmitting || isLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}>
                Salvar
              </Button>
            </div>
          </Stack>
        </Col>
      </Row>
    </Form>
  )
}

export default FormTogglePassword
